// @flow
import React from 'react';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import NumericInput from 'react-numeric-input';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

import Responsive from 'app/components/responsive/Responsive';
import Column from 'app/components/commons/Column';

import { TableRow, TableHead, TableCell } from './Table';

type Props = {
  onCrunchTeams: (strId?: string) => void,
  playerExposures: Array<Object>,
  exposureVariations: Array<{ idStr: string, value: number }>,
  updateExposureVariation: (idStr: string, value: number | undefined) => void
};

const header = [
  {
    field: 'name',
    label: 'NAME',
    size: 5,
    sortable: false
  },
  {
    field: 'insiderScoreUpdated',
    label: 'PROJECTION',
    mobileLabel: 'PROJ',
    size: 2,
    sortable: false
  },
  {
    field: 'exposure',
    label: 'EXPOSURE%',
    mobileLabel: 'EXP%',
    size: 3,
    sortable: false
  },
  {
    field: 'actions',
    label: 'APPLY?',
    size: 2,
    sortable: false
  }
];

const Buttons = styled.div({
  display: 'flex'
});

const ExposureMgmtTable = ({
  onCrunchTeams,
  playerExposures,
  exposureVariations,
  updateExposureVariation
}: Props) => {
  const Row = ({ index, style }: RowProps) => {
    const { idStr, name, insiderScoreUpdated, exposure } = playerExposures[index];
    const [localPlayerExposure, setLocalPlayerExposure] = React.useState(exposure);
    const [editing, setEditing] = React.useState(false);

    const playerExposureVariation = exposureVariations.find(exp => exp.idStr === idStr);
    const playerExposure = playerExposureVariation ? playerExposureVariation.value : exposure;

    const handleApply = () => {
      onCrunchTeams(idStr);
    };

    const handleCancel = () => {
      updateExposureVariation(idStr);
      setEditing(false);
    };

    const handleChange = val => {
      setLocalPlayerExposure(val);
      setEditing(true);
    };

    const handleBlur = () => {
      if (exposure !== localPlayerExposure) updateExposureVariation(idStr, localPlayerExposure);
      setEditing(false);
    };

    return (
      <TableRow className="is-mobile" key={idStr} style={style}>
        <TableCell className="is-5">{name}</TableCell>
        <TableCell className="is-2">{insiderScoreUpdated}</TableCell>
        <TableCell className="is-3">
          <NumericInput
            min={0}
            max={100}
            step={1}
            size={4}
            value={editing ? localPlayerExposure : playerExposure}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </TableCell>
        <TableCell className="is-2">
          {playerExposure !== exposure && (
            <Buttons>
              <button type="button" onClick={handleApply}>
                <FontAwesomeIcon icon={faCheck} />
              </button>
              <button type="button" onClick={handleCancel}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </Buttons>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div>
      <TableHead className="is-mobile" hasScrollbar>
        {header.map(({ field, label, mobileLabel, size }) => (
          <Column key={field} className={`is-${size}`}>
            <Responsive tabletLandscape desktop>
              {label}
            </Responsive>
            <Responsive mobile>{mobileLabel || label}</Responsive>
          </Column>
        ))}
      </TableHead>
      <div style={{ flex: '1 1 auto' }}>
        <AutoSizer disableHeight>
          {({ width }) => (
            <List height={800} itemCount={playerExposures.length} itemSize={40} width={width}>
              {Row}
            </List>
          )}
        </AutoSizer>
      </div>
    </div>
  );
};

export default ExposureMgmtTable;
